/**
 * What we know about whether an audience can actually receive a WhatsApp message.
 *
 * The check itself happens on the session host; this module is about the answer's shelf life
 * and about turning a pile of per-contact facts into the one sentence the composer shows:
 * "412 of 500 reachable". That sentence has to appear *before* credits are committed, which
 * is the whole point — an invalid number in a broadcast is a credit spent on nothing and a
 * failed send against a number WhatsApp is already scoring.
 *
 * Nothing here calls WhatsApp. That separation is deliberate and load-bearing: the composer
 * re-previews on every keystroke, and a preview that asked WhatsApp about five hundred
 * numbers each time somebody edited a word would be a far better way to get a number banned
 * than any broadcast. The preview reads this cache; a check is an explicit act.
 */

export type Reachability = 'reachable' | 'unreachable' | 'unknown'

/**
 * How long an answer is trusted.
 *
 * People join and leave WhatsApp, and re-checking an audience of thousands weekly would cost
 * more in lookups than the wasted credits it saves. A month is long enough to make the cache
 * worth having and short enough that a list is re-validated roughly once per campaign cycle.
 */
export const REACHABILITY_TTL_DAYS = 30

/**
 * How many numbers one check may ask about.
 *
 * Written out here as well as in the session host's `reachability.ts`, for the same reason
 * `normalize.ts` writes out the media-type list rather than importing it: the host is a
 * separate package with its own dependency tree. The two have to be changed together, and
 * the host's own 400 is what catches it if they are not.
 */
export const MAX_NUMBERS_PER_CHECK = 1000

/**
 * The most unchecked numbers the composer will check on its own. Larger than this stays an
 * explicit press of the button: a lookup burst against the WhatsApp socket is the one thing here
 * that can hurt a number, so the automatic path is kept to audiences small enough that it reads
 * as ordinary traffic, and a big list is left for a person to decide to spend the queries on.
 */
export const AUTO_CHECK_MAX_UNKNOWN = 500

const DAY_MS = 86_400_000

export interface CachedReachability {
  /** `null` means never checked, which is not the same as checked and found absent. */
  reachable: boolean | null
  checkedAt: Date | null
}

/**
 * What we currently believe about one contact.
 *
 * A stale answer reads as `unknown` rather than as its last value, so an audience that has
 * not been looked at since spring is reported as unchecked instead of quietly reusing facts
 * about who was on WhatsApp a year ago.
 */
export function reachabilityOf(cached: CachedReachability, now: Date): Reachability {
  if (cached.reachable === null || !cached.checkedAt) return 'unknown'
  const ageDays = (now.getTime() - cached.checkedAt.getTime()) / DAY_MS
  if (ageDays > REACHABILITY_TTL_DAYS) return 'unknown'
  return cached.reachable ? 'reachable' : 'unreachable'
}

export interface ReachabilitySummary {
  total: number
  reachable: number
  unreachable: number
  unknown: number
}

export function summariseReachability(states: Reachability[]): ReachabilitySummary {
  return {
    total: states.length,
    reachable: states.filter((s) => s === 'reachable').length,
    unreachable: states.filter((s) => s === 'unreachable').length,
    unknown: states.filter((s) => s === 'unknown').length,
  }
}

/**
 * The line the composer shows.
 *
 * An unchecked audience says so rather than reporting zero reachable, because "0 of 500
 * reachable" and "500 not yet checked" would lead a customer to two opposite conclusions and
 * only one of them is true.
 */
export function reachabilitySentence(summary: ReachabilitySummary): string {
  if (summary.total === 0) return 'No recipients yet.'
  if (summary.reachable === 0 && summary.unreachable === 0) {
    return `${summary.total} recipients, none checked against WhatsApp yet.`
  }

  const checked = summary.reachable + summary.unreachable
  const head = `${summary.reachable} of ${checked} reachable`
  if (summary.unknown === 0) return `${head}.`
  return `${head}, and ${summary.unknown} not yet checked.`
}

/**
 * Splits an audience into who a send should go to and who it should skip.
 *
 * Only a confirmed `unreachable` is dropped. An unchecked recipient is always kept: the cost
 * of a wasted credit is one credit, and the cost of silently not messaging a real customer
 * because nobody ran a check is the account.
 */
export function excludeUnreachable<T extends { phone: string }>(
  recipients: T[],
  states: Map<string, Reachability>,
): { keep: T[]; skipped: T[] } {
  const keep: T[] = []
  const skipped: T[] = []
  for (const recipient of recipients) {
    if (states.get(recipient.phone) === 'unreachable') skipped.push(recipient)
    else keep.push(recipient)
  }
  return { keep, skipped }
}
